import { motion } from "framer-motion";
import { Quote, CheckCircle2, ArrowUpRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FOUNDER, COMPANY } from "@/lib/data";

const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

export default function Founder() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#0B1220] via-[#0A2160] to-[#0F3D8C]" />
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(212,175,55,0.35),_transparent_55%)]" />

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10 text-white">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-[#D4AF37] via-[#F4E5A1] to-[#D4AF37] opacity-30 blur-2xl" />
              <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border-4 border-white/10 bg-white/[0.04] backdrop-blur-xl">
                {FOUNDER.photo ? (
                  <img
                    src={FOUNDER.photo}
                    alt={`${FOUNDER.name}, ${FOUNDER.role} at Globe Vista Overseas Education`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B99328] flex items-center justify-center text-[#0F172A] text-4xl font-heading font-semibold shadow-2xl">
                      SM
                    </div>
                    <div className="mt-6 text-xs tracking-[0.25em] uppercase text-white/50 flex items-center gap-2">
                      <User className="w-3.5 h-3.5" /> Portrait Coming Soon
                    </div>
                  </div>
                )}
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-[#0F172A] px-4 py-2 rounded-full text-xs font-bold tracking-[0.15em] uppercase shadow-xl">
                10+ Years
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="text-xs tracking-[0.25em] uppercase text-[#FCD34D] font-bold mb-4">
              Meet the Founder
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.05]">
              <span className="font-serif italic text-gradient-gold">{FOUNDER.name}</span>
            </h2>
            <div className="mt-2 text-sm tracking-[0.2em] uppercase text-white/60">
              {FOUNDER.role} · Globe Vista
            </div>

            <div className="mt-8 relative">
              <Quote className="absolute -top-2 -left-1 w-10 h-10 text-[#D4AF37]/30" />
              <p className="text-lg text-white/85 leading-relaxed pl-10">{FOUNDER.bio}</p>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {FOUNDER.credentials.map((c) => (
                <div
                  key={c}
                  className="flex items-start gap-2.5 p-3 rounded-xl bg-white/[0.06] border border-white/10"
                >
                  <CheckCircle2 className="w-4 h-4 text-[#FCD34D] shrink-0 mt-0.5" />
                  <span className="text-sm text-white/85">{c}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => scrollTo("#contact")}
                data-testid="founder-book-btn"
                className="rounded-full bg-[#D4AF37] hover:bg-[#B99328] text-[#0F172A] font-semibold px-7 py-6 group"
              >
                Book a Session with {FOUNDER.name.split(" ")[0]} Sir
                <ArrowUpRight className="ml-1 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
              <a
                href={COMPANY.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-instagram-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 px-7 py-3.5 font-medium"
              >
                Follow on Instagram
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
